import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { Col, Row } from "reactstrap";
import { faCircleXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import CustomSelect from "components/customs/dropdown";
import {
  canEditRoles,
  congRoles,
  fundSourceOptions,
  institutionTypes,
} from "utility/constants";
import { ATC_ALLOCATION_TYPE_ID } from "constants/budgetAllocation";
import {
  addProjectDropdownStyles,
  addProjectSearchInputStyle,
} from "constants/style";
import {
  Toast,
  getFormattedContactNumber,
  getFormattedOptions,
} from "utility/helpers";
import FormGenerator from "components/customs/form-generator/input";
import {
  fetchInstitutions,
  fetchMunicipalities,
  fetchProvinces,
} from "services/services.projects";
import CustomCreatableSelect from "components/customs/creatable-select";
import { fetchAtcOptions } from "services/services.directory";
import { genericGet } from "services/services.general";
import useAuth from "hooks/useAuth";
import LoadingScreen from "components/customs/loading-screen";
import { FormLabel, RequiredIndicator } from "assets/js/Styles";
import {
  AGENCY_TESDA_ID,
  AGENCY_DOH_ID,
  AGENCY_DPWH_ID,
  AGENCY_DBM_ID,
  TESDA_PROGRAMS,
  DBM_PROGRAM_LGSF_ID,
} from "constants/agency";
import DropdownSearch from "components/customs/dropdownSearch";
import styled from "styled-components";
import Others from "./Others";
import CoursesTable from "./CoursesTable";
import { allocationTypeOptions } from "./constants";
import {
  getAllocationTypeOptions,
  getInstitutionParam,
  isAllocationTypeDisabled,
  isATCDropdownShown,
  isImplementingOfficeShown,
  isOtherFormShown,
  isProjectTitleShown,
  isTrackingCodeShown,
} from "./utility";
import SupportingDocumentsSection from "./SupportingDocuments";
import FundSourceSection from "./FundSourceSection";

const HouseMemberSection = styled(Row)`
  margin-top: 1rem;
  display: flex;
  justify-content: space-between;
`;

const HouseMemberColRight = styled.div`
  display: grid;
  grid-template-columns: 1fr 107px;

  @media screen and (max-width: 575.98px) {
    grid-template-columns: 1fr;
    row-gap: 12px;
  }
`;

const DeleteRowIcon = styled.div`
  width: 107px;
  padding-left: calc(var(--bs-gutter-x) * 0.5);
  display: flex;
  align-items: center;
`;

const AddProgramForm = ({
  agency,
  handleSearchInputOnChange,
  index,
  handleCongressmanSearch,
  recipient,
  projectRecipients,
  handleDeleteRow,
  setProjectRecipients,
  selectedProject,
  setUploadedFiles,
  uploadedFiles,
  isFormResetted,
  setIsFormResetted,
  regionOptions,
  selectedCourses,
  setSelectedCourses,
}) => {
  const { user } = useAuth();
  const [facilityType, setFacilityType] = React.useState(null);
  const [facilityOptions, setFacilityOptions] = React.useState([]);
  const [selectedInstitution, setSelectedInstitution] = React.useState(null);
  const [selectedTVI, setSelectedTVI] = React.useState(null);
  const [TVIOptions, setTVIOptions] = React.useState([]);
  const [TVICourses, setTVICourses] = React.useState([]);

  const [selectedProvince, setSelectedProvince] = React.useState(null);
  const [provinceOptions, setProvinceOptions] = React.useState([]);
  const [selectedMunicipality, setSelectedMunicipality] = React.useState(null);
  const [municipalityOptions, setMunicipalityOptions] = React.useState([]);

  const [atcOptions, setAtcOptions] = React.useState([]);
  const [selectedFundSource, setSelectedFundSource] = React.useState([]);

  const [selectedAtcPartner, setSelectedAtcPartner] = React.useState("");

  const [selectedPasscodeValidity, setSelectedPasscodeValidity] =
    React.useState(null);

  const role = user?.type;
  const isRoleCongressman = congRoles.includes(role);

  const fetchAllInstitutions = async () => {
    await fetchInstitutions({
      param: getInstitutionParam({
        agency,
        recipient,
        facilityType,
      }),
      callback: (data) => {
        if (data.length) {
          setFacilityOptions(
            data.map((hospital) => ({
              ...hospital,
              id: hospital.id,
              label: hospital.name,
              value: hospital.id,
            }))
          );
        }
      },
    });
  };

  const fetchAllProvinces = async () => {
    await fetchProvinces({
      param: recipient?.recipientName?.region?.value,
      callback: (data) => {
        const options = data.map(({ id, name, omegaId }) => ({
          label: name,
          value: id,
          omegaId,
        }));

        options.unshift({
          label: "All Provinces",
          value: "All Provinces",
        });

        setSelectedProvince(options[0]);
        setProvinceOptions(options);
      },
    });
  };

  const fetchAllMunicipalities = async () => {
    const municipalityId =
      typeof selectedProvince?.value === "number"
        ? selectedProvince?.value
        : "";

    await fetchMunicipalities({
      param: municipalityId,
      callback: (data) => {
        if (data?.length) {
          const options = data.map(({ id, name, omegaId }) => ({
            label: name,
            value: id,
            omegaId,
          }));

          options.unshift({
            label: "All Municipalities",
            value: "All Municipalities",
          });

          setSelectedMunicipality(options[0]);
          setMunicipalityOptions(options);
        } else {
          setMunicipalityOptions([]);
        }
      },
    });
  };

  const fetchTViCourses = () => {
    genericGet({
      url: "/tvi",
      params: {
        provinceId: selectedProvince.value,
        limit: -1,
      },
      callback: (data) => {
        if (data.rows?.length) {
          const options = getFormattedOptions("name", data.rows);

          setTVIOptions(options);
        } else {
          setTVIOptions([]);
        }
      },
    });
  };

  const resetForm = (allocType) => {
    setSelectedInstitution(null);
    setSelectedProvince(null);
    setSelectedMunicipality(null);
    setFacilityType(null);
    setSelectedFundSource(null);

    const newProjectRecipients = [...projectRecipients];

    newProjectRecipients[index] = {
      ...newProjectRecipients[index],
      justification: "",
      allocationType: allocType,
      budgetAllocation: "",
      projectTitle: "",
      institution: "",
      otherInfo: "",
      slots: "",
      implementingOffice: "",
      notes: null,
      allocationContactNumber: "",
      allocationContactName: "",
      recipientName: {
        ...newProjectRecipients[index].recipientName,
        region: null,
      },
      isRequestList: agency === AGENCY_DPWH_ID && allocType === 3,
    };

    setProjectRecipients(newProjectRecipients);
    setIsFormResetted(false);
  };

  const fetchAllATC = async () => {
    await fetchAtcOptions({
      params: {},
      callback: (data) => {
        const options = data
          ?.filter((atc) => atc.principal)
          ?.map((atc) => ({
            ...atc,
            label: `${atc?.last_name}, ${atc?.first_name}`,
            value: atc?.id,
          }));

        if (options.length !== 0) {
          const firstOption = options[0];

          setProjectRecipients((currentRecipients) => {
            const newProjectRecipients = [...currentRecipients];

            newProjectRecipients[index] = {
              ...newProjectRecipients[index],
              // eslint-disable-next-line max-len
              allocationName: `${firstOption?.last_name}, ${firstOption?.first_name}`,
              allocationContactNumber: getFormattedContactNumber(
                firstOption?.principal?.atc_partner_contact
              ),
              allocationContactName: firstOption?.principal
                ?.atc_partner_last_name
                ? // eslint-disable-next-line max-len
                  `${firstOption?.principal?.atc_partner_first_name} ${firstOption?.principal?.atc_partner_last_name}`
                : firstOption?.principal?.atc_partner_first_name,
              allocationReferenceKey: firstOption?.principal?.id,
              allocation: {
                atcId: firstOption?.id,
                principalId: firstOption?.principal?.id,
                managerFirstName:
                  firstOption?.principal?.atc_partner_manager_first_name,
                managerLastName:
                  firstOption?.principal?.atc_partner_manager_last_name,
                managerContact:
                  firstOption?.principal?.atc_partner_manager_contact,
              },
            };

            return newProjectRecipients;
          });

          setSelectedAtcPartner(
            `${firstOption?.principal?.atc_partner_first_name || ""} ${
              firstOption?.principal?.atc_partner_last_name || ""
            }`.trim()
          );
        }

        setAtcOptions(options);
      },
    });
  };

  const fetchAllCourses = () => {
    genericGet({
      url: `/tvi/${selectedTVI?.value}/courses`,
      callback: (data) => {
        setTVICourses(data.rows);
      },
    });
  };

  const resetSelectedATC = () => {
    const newProjectRecipients = [...projectRecipients];

    newProjectRecipients[index] = {
      ...newProjectRecipients[index],
      allocationName: "",
      allocationContactNumber: "",
      allocationContactName: "",
      allocationReferenceKey: "",
      allocation: "",
    };

    setProjectRecipients(newProjectRecipients);
  };

  const handleDeleteAllFiles = () => {
    setUploadedFiles([]);
  };

  useEffect(() => {
    if (selectedProvince) {
      fetchAllMunicipalities();
      setTVIOptions([]);
      setTVICourses([]);
      setSelectedCourses([]);
      setSelectedTVI(null);

      if (
        typeof selectedProvince?.value === "number" &&
        agency === AGENCY_TESDA_ID
      ) {
        fetchTViCourses();
      }

      if (selectedProvince.value === "All Provinces") {
        setSelectedMunicipality({
          label: "All Municipalities",
          value: "All Municipalities",
        });
      }
    }
  }, [selectedProvince]);

  useEffect(() => {
    if (recipient?.recipientName?.region) {
      fetchAllProvinces();

      setTVIOptions([]);
      setTVICourses([]);
      setSelectedCourses([]);
      setSelectedTVI(null);

      if (agency === 8) fetchAllInstitutions();
    }
  }, [recipient?.recipientName?.region]);

  useEffect(() => {
    if (recipient?.allocationType === ATC_ALLOCATION_TYPE_ID) fetchAllATC();
    else resetSelectedATC();
  }, [recipient?.allocationType]);

  useEffect(() => {
    if (facilityType) {
      fetchAllInstitutions();
      setSelectedInstitution(null);
    }
  }, [facilityType]);

  useEffect(() => {
    if (agency === AGENCY_TESDA_ID) {
      fetchAllInstitutions();
    }

    if (agency === 8) {
      const newProjectRecipients = [...projectRecipients];

      newProjectRecipients[index] = {
        ...newProjectRecipients[index],
        facilityType: institutionTypes[0],
      };

      setProjectRecipients(newProjectRecipients);
      setFacilityType(institutionTypes[0]);
    }
  }, [agency]);

  useEffect(() => {
    if (isFormResetted) {
      resetForm();
    }
  }, [isFormResetted]);

  useEffect(() => {
    if (selectedTVI) {
      setSelectedCourses([]);
      fetchAllCourses();
    }
  }, [selectedTVI]);

  if (!user) {
    return <LoadingScreen />;
  }

  const generateForm = () => {
    let agencyForm = null;
    let additionalFields = null;

    switch (agency) {
      case AGENCY_DOH_ID:
        agencyForm = (
          <>
            <HouseMemberSection>
              <Col
                xs="12"
                md="6"
              >
                <FormLabel>
                  {"Type of Facility"}
                  {RequiredIndicator}
                </FormLabel>
                <p>{"Provide the type of facility"}</p>
              </Col>
              <Col
                xs="12"
                md="6"
              >
                <HouseMemberColRight>
                  <CustomSelect
                    placeholder="Type of Facility"
                    styles={addProjectDropdownStyles}
                    options={institutionTypes}
                    onChange={(option) => {
                      const newProjectRecipients = [...projectRecipients];

                      newProjectRecipients[index] = {
                        ...newProjectRecipients[index],
                        facilityType: option,
                      };

                      setProjectRecipients(newProjectRecipients);
                      setFacilityType(option);
                    }}
                    value={facilityType}
                  />
                </HouseMemberColRight>
              </Col>
            </HouseMemberSection>

            <HouseMemberSection>
              <Col
                xs="12"
                md="6"
              >
                <FormLabel>
                  {"Name of Facility"}
                  {RequiredIndicator}
                </FormLabel>
                <p>{"Provide the name of facility"}</p>
              </Col>
              <Col
                xs="12"
                md="6"
              >
                <HouseMemberColRight>
                  <CustomCreatableSelect
                    placeholder="Name of Facility"
                    styles={addProjectDropdownStyles}
                    options={facilityOptions}
                    onChange={(option) => {
                      const newProjectRecipients = [...projectRecipients];

                      newProjectRecipients[index] = {
                        ...newProjectRecipients[index],
                        institution: option,
                      };

                      setProjectRecipients(newProjectRecipients);
                      setSelectedInstitution(option);
                    }}
                    value={selectedInstitution}
                  />
                </HouseMemberColRight>
              </Col>
            </HouseMemberSection>
          </>
        );
        break;

      case AGENCY_TESDA_ID:
        agencyForm =
          TESDA_PROGRAMS.includes(selectedProject) &&
          projectRecipients[index].allocationType !== ATC_ALLOCATION_TYPE_ID ? (
            <HouseMemberSection>
              <Col
                xs="12"
                md="6"
              >
                <FormLabel>
                  {"Name of TVI"}
                  {RequiredIndicator}
                </FormLabel>
                <p>{"Provide the name of the TVI"}</p>
              </Col>
              <Col
                xs="12"
                md="6"
              >
                <HouseMemberColRight>
                  <CustomSelect
                    placeholder="Name of TVI"
                    styles={addProjectDropdownStyles}
                    options={TVIOptions}
                    disabled={!selectedProvince}
                    onChange={(option) => {
                      const newProjectRecipients = [...projectRecipients];

                      newProjectRecipients[index] = {
                        ...newProjectRecipients[index],
                        tvi: option?.value,
                      };

                      setProjectRecipients(newProjectRecipients);
                      setSelectedTVI(option);
                    }}
                    value={selectedTVI}
                  />
                </HouseMemberColRight>
              </Col>
            </HouseMemberSection>
          ) : null;
        break;

      default:
        break;
    }

    switch (agency) {
      case AGENCY_TESDA_ID:
        additionalFields = TESDA_PROGRAMS.includes(selectedProject) ? (
          <SupportingDocumentsSection
            uploadedFiles={uploadedFiles}
            setUploadedFiles={setUploadedFiles}
          />
        ) : null;
        break;

      case AGENCY_DPWH_ID:
        additionalFields =
          projectRecipients[index].allocationType !== ATC_ALLOCATION_TYPE_ID ? (
            <>
              {canEditRoles.includes(role) && (
                <FundSourceSection
                  index={index}
                  fundSourceOptions={fundSourceOptions}
                  projectRecipients={projectRecipients}
                  setProjectRecipients={setProjectRecipients}
                  setSelectedFundSource={setSelectedFundSource}
                  selectedFundSource={selectedFundSource}
                />
              )}

              <SupportingDocumentsSection
                uploadedFiles={uploadedFiles}
                setUploadedFiles={setUploadedFiles}
              />
            </>
          ) : null;
        break;

      case AGENCY_DBM_ID:
        additionalFields =
          selectedProject === DBM_PROGRAM_LGSF_ID &&
          canEditRoles.includes(role) &&
          projectRecipients[index].allocationType !== ATC_ALLOCATION_TYPE_ID ? (
            <FundSourceSection
              index={index}
              fundSourceOptions={fundSourceOptions}
              projectRecipients={projectRecipients}
              setProjectRecipients={setProjectRecipients}
              setSelectedFundSource={setSelectedFundSource}
              selectedFundSource={selectedFundSource}
            />
          ) : null;
        break;

      default:
        break;
    }

    return (
      <>
        <HouseMemberSection>
          <Col
            xs="12"
            md="6"
          >
            <FormLabel>
              {"House Member"}
              {RequiredIndicator}
            </FormLabel>
            <p>{"Provide the house member's name"}</p>
          </Col>
          <Col
            xs="12"
            md="6"
          >
            <HouseMemberColRight>
              <DropdownSearch
                placeholder="House Member"
                onChange={(option) => {
                  handleSearchInputOnChange(option, index);
                }}
                loadOptions={handleCongressmanSearch}
                styles={addProjectSearchInputStyle}
                value={recipient?.recipientName}
                isDisabled={isRoleCongressman}
              />

              {projectRecipients.length > 1 ? (
                <DeleteRowIcon
                  aria-hidden
                  onClick={(e) =>
                    projectRecipients.length > 1
                      ? handleDeleteRow(e, index)
                      : null
                  }
                >
                  {!congRoles.includes(role) && (
                    <FontAwesomeIcon
                      size="xl"
                      color={
                        projectRecipients.length < 2 ? "#9FA5AA" : "#4252C9"
                      }
                      icon={faCircleXmark}
                      style={{
                        cursor:
                          projectRecipients.length < 2 ? "default" : "pointer",
                      }}
                    />
                  )}
                </DeleteRowIcon>
              ) : null}
            </HouseMemberColRight>
          </Col>
        </HouseMemberSection>

        {isProjectTitleShown(agency, selectedProject, recipient) && (
          <HouseMemberSection>
            <Col
              xs="12"
              md="6"
            >
              <FormLabel>
                {"Project Title"}
                {RequiredIndicator}
              </FormLabel>
              <p>{"Provide the project title"}</p>
            </Col>
            <Col
              xs="12"
              md="6"
            >
              <HouseMemberColRight>
                <FormGenerator
                  type="text"
                  placeholder="Project Title"
                  value={recipient?.institution || ""}
                  onChange={(event) => {
                    const {
                      target: { value },
                    } = event;

                    const newProjectRecipients = [...projectRecipients];

                    newProjectRecipients[index] = {
                      ...newProjectRecipients[index],
                      institution: value,
                    };

                    setProjectRecipients(newProjectRecipients);
                  }}
                />
              </HouseMemberColRight>
            </Col>
          </HouseMemberSection>
        )}

        {/* START DEPARTMENT OF BUDGET AND MANAGEMENT */}
        {isTrackingCodeShown(agency, selectedProject, recipient) && (
          <HouseMemberSection>
            <Col
              xs="12"
              md="6"
            >
              <FormLabel>
                {"LGSF Tracking Code"}
                {RequiredIndicator}
              </FormLabel>
              <p>{"Provide the  LGSF tracking code"}</p>
            </Col>
            <Col
              xs="12"
              md="6"
            >
              <HouseMemberColRight>
                <FormGenerator
                  type="text"
                  placeholder="LGSF Tracking Code"
                  value={recipient?.otherInfo || ""}
                  onChange={(event) => {
                    const {
                      target: { value },
                    } = event;

                    const newProjectRecipients = [...projectRecipients];

                    newProjectRecipients[index] = {
                      ...newProjectRecipients[index],
                      otherInfo: value,
                    };

                    setProjectRecipients(newProjectRecipients);
                  }}
                />
              </HouseMemberColRight>
            </Col>
          </HouseMemberSection>
        )}
        {/* END DEPARTMENT OF BUDGET AND MANAGEMENT */}

        {!isRoleCongressman && (
          <HouseMemberSection>
            <Col
              xs="12"
              md="6"
            >
              <FormLabel>
                {"Allocation Type"}
                {RequiredIndicator}
              </FormLabel>
              <p>{"Provide the allocation type"}</p>
            </Col>
            <Col
              xs="12"
              md="6"
            >
              <HouseMemberColRight>
                <CustomSelect
                  placeholder="Allocation Type"
                  styles={addProjectDropdownStyles}
                  options={getAllocationTypeOptions({
                    role,
                    congressman: projectRecipients[index]?.recipientName?.value,
                  })}
                  value={allocationTypeOptions.find(
                    (type) =>
                      type.value === projectRecipients[index]?.allocationType
                  )}
                  onChange={({ value }) => {
                    const newProjectRecipients = [...projectRecipients];

                    newProjectRecipients[index] = {
                      ...newProjectRecipients[index],
                      allocationType: value,
                    };

                    if (value === 3) {
                      newProjectRecipients[index] = {
                        ...newProjectRecipients[index],
                        allocationType: value,
                        allocationContactName: "",
                        allocationContactNumber: null,
                      };
                    }

                    setProjectRecipients(newProjectRecipients);

                    resetForm(value);
                    handleDeleteAllFiles();
                  }}
                  isDisabled={isAllocationTypeDisabled({
                    role,
                    congressman: projectRecipients[index]?.recipientName?.value,
                  })}
                />
              </HouseMemberColRight>
            </Col>
          </HouseMemberSection>
        )}

        {isOtherFormShown(recipient) && agency === AGENCY_DPWH_ID && (
          <>
            <HouseMemberSection>
              <Col
                xs="12"
                md="6"
              >
                <FormLabel>{"Passcode Validity"}</FormLabel>
                <p>{"Provide the passcode validity"}</p>
              </Col>
              <Col
                xs="12"
                md="6"
              >
                <HouseMemberColRight>
                  <FormGenerator
                    type="datetime"
                    placeholder="Passcode Validity"
                    selected={selectedPasscodeValidity}
                    minDate={new Date()}
                    dateFormat="MMM-dd-yyyy"
                    onChange={(value) => {
                      setSelectedPasscodeValidity(value);

                      const newProjectRecipients = [...projectRecipients];

                      newProjectRecipients[index] = {
                        ...newProjectRecipients[index],
                        passcodeValidity: value.toISOString(),
                      };
                      setProjectRecipients(newProjectRecipients);
                    }}
                  />
                </HouseMemberColRight>
              </Col>
            </HouseMemberSection>
            <Others
              allocationName={
                projectRecipients[index]?.allocationContactName || ""
              }
              contact={projectRecipients[index]?.allocationContactNumber || ""}
              onChange={(updates) => {
                const newProjectRecipients = [...projectRecipients];

                newProjectRecipients[index] = {
                  ...newProjectRecipients[index],
                  ...updates,
                };
                setProjectRecipients(newProjectRecipients);
              }}
            />
          </>
        )}

        <HouseMemberSection>
          <Col
            xs="12"
            md="6"
          >
            <FormLabel>
              {"Amount Requested"}
              {RequiredIndicator}
            </FormLabel>
            <p>{"Provide the amount requested"}</p>
          </Col>
          <Col
            xs="12"
            md="6"
          >
            <HouseMemberColRight>
              <FormGenerator
                type="number"
                placeholder="Amount"
                value={recipient?.budgetAllocation}
                disabled={agency === AGENCY_TESDA_ID}
                decimalSeparator="."
                thousandSeparator=","
                onValueChange={(value) => {
                  const { floatValue } = value;

                  const newProjectRecipients = [...projectRecipients];

                  newProjectRecipients[index] = {
                    ...newProjectRecipients[index],
                    budgetAllocation: floatValue,
                  };

                  setProjectRecipients(newProjectRecipients);
                }}
              />
            </HouseMemberColRight>
          </Col>
        </HouseMemberSection>

        <HouseMemberSection>
          <Col
            xs="12"
            md="6"
          >
            <FormLabel>
              {"Recommended Implementing Agency"}
              {RequiredIndicator}
            </FormLabel>
            <p>{"Provide the recommended implementing agency"}</p>
          </Col>

          <Col
            xs="12"
            md="6"
          >
            <HouseMemberColRight>
              <CustomSelect
                placeholder="Please Select a Region"
                styles={addProjectDropdownStyles}
                options={regionOptions}
                onChange={(option) => {
                  const newProjectRecipients = [...projectRecipients];

                  newProjectRecipients[index] = {
                    ...newProjectRecipients[index],
                    recipientName: {
                      ...newProjectRecipients[index].recipientName,
                      region: option,
                    },
                  };

                  setProjectRecipients(newProjectRecipients);
                }}
                value={
                  recipient?.recipientName?.region
                    ? {
                        value: recipient?.recipientName?.region?.value,
                        label: recipient?.recipientName?.region?.label,
                      }
                    : null
                }
              />
            </HouseMemberColRight>
          </Col>
        </HouseMemberSection>

        {isImplementingOfficeShown(agency, recipient) && (
          <HouseMemberSection>
            <Col
              xs="12"
              md="6"
            >
              <FormLabel>
                {"Implementing Office"}
                {RequiredIndicator}
              </FormLabel>
              <p>{"Provide the implementing office"}</p>
            </Col>
            <Col
              xs="12"
              md="6"
            >
              <HouseMemberColRight>
                <FormGenerator
                  type="text"
                  placeholder="Implementing Office"
                  value={recipient?.implementingOffice || ""}
                  onChange={(event) => {
                    const {
                      target: { value },
                    } = event;

                    const newProjectRecipients = [...projectRecipients];

                    newProjectRecipients[index] = {
                      ...newProjectRecipients[index],
                      implementingOffice: value,
                    };

                    setProjectRecipients(newProjectRecipients);
                  }}
                />
              </HouseMemberColRight>
            </Col>
          </HouseMemberSection>
        )}

        <HouseMemberSection>
          <Col
            xs="12"
            md="6"
          >
            <FormLabel>
              {"Province"}
              {agency === AGENCY_TESDA_ID && RequiredIndicator}
            </FormLabel>
            <p>{"Provide the province in your region"}</p>
          </Col>
          <Col
            xs="12"
            md="6"
          >
            <HouseMemberColRight>
              <CustomSelect
                placeholder="Please Select a Province"
                styles={addProjectDropdownStyles}
                options={provinceOptions}
                onChange={(option) => {
                  const newProjectRecipients = [...projectRecipients];

                  newProjectRecipients[index] = {
                    ...newProjectRecipients[index],
                    Province: option,
                  };

                  setProjectRecipients(newProjectRecipients);
                  setSelectedProvince(option);
                }}
                value={selectedProvince}
              />
            </HouseMemberColRight>
          </Col>
        </HouseMemberSection>

        <HouseMemberSection>
          <Col
            xs="12"
            md="6"
          >
            <FormLabel>{"Municipality"}</FormLabel>
            <p>{"Provide the municipality in your province"}</p>
          </Col>
          <Col
            xs="12"
            md="6"
          >
            <HouseMemberColRight>
              <CustomSelect
                placeholder="Please Select a Municipality"
                styles={addProjectDropdownStyles}
                options={municipalityOptions}
                onChange={(option) => {
                  const newProjectRecipients = [...projectRecipients];

                  newProjectRecipients[index] = {
                    ...newProjectRecipients[index],
                    Municipality: option,
                  };

                  setProjectRecipients(newProjectRecipients);
                  setSelectedMunicipality(option);
                }}
                value={selectedMunicipality}
                isDisabled={!recipient?.Province}
              />
            </HouseMemberColRight>
          </Col>
        </HouseMemberSection>

        {canEditRoles.includes(role) && (
          <HouseMemberSection>
            <Col
              xs="12"
              md="6"
            >
              <FormLabel>{"Editor Notes"}</FormLabel>
              <p>{"Provide the editor notes"}</p>
            </Col>
            <Col
              xs="12"
              md="6"
            >
              <HouseMemberColRight>
                <CustomCreatableSelect
                  isMulti
                  placeholder="Editor Notes"
                  styles={addProjectDropdownStyles}
                  onChange={(option) => {
                    const newProjectRecipients = [...projectRecipients];

                    newProjectRecipients[index] = {
                      ...newProjectRecipients[index],
                      notes: option,
                    };

                    setProjectRecipients(newProjectRecipients);
                  }}
                  value={recipient?.notes}
                />
              </HouseMemberColRight>
            </Col>
          </HouseMemberSection>
        )}

        {isATCDropdownShown(recipient) && (
          <>
            <HouseMemberSection>
              <Col
                xs="12"
                md="6"
              >
                <FormLabel>
                  {"ATC"}
                  {RequiredIndicator}
                </FormLabel>
                <p>{"Provide the ATC Branch"}</p>
              </Col>
              <Col
                xs="12"
                md="6"
              >
                <HouseMemberColRight>
                  <CustomSelect
                    name="atc"
                    placeholder="ATC"
                    styles={addProjectDropdownStyles}
                    options={atcOptions}
                    value={
                      atcOptions.find(
                        (option) =>
                          option.id ===
                          projectRecipients[index].allocation.atcId
                      ) || null
                    }
                    onChange={(value) => {
                      if (
                        !value?.principal?.atc_partner_first_name ||
                        value?.principal?.atc_partner_contact?.length < 10
                      ) {
                        Toast({
                          type: 4,
                          content:
                            "ATC contact name and number cannot be empty",
                        });

                        return;
                      }

                      const newProjectRecipients = [...projectRecipients];

                      newProjectRecipients[index] = {
                        ...newProjectRecipients[index],
                        // eslint-disable-next-line max-len
                        allocationName: `${value?.last_name}, ${value?.first_name}`,
                        allocationContactNumber: getFormattedContactNumber(
                          value?.principal?.atc_partner_contact
                        ),
                        allocationContactName: value?.principal
                          ?.atc_partner_last_name
                          ? // eslint-disable-next-line max-len
                            `${value?.principal?.atc_partner_first_name} ${value?.principal?.atc_partner_last_name}`
                          : value?.principal?.atc_partner_first_name,
                        allocationReferenceKey: value?.principal?.id,
                        allocation: {
                          atcId: value?.id,
                          principalId: value?.principal?.id,
                          managerFirstName:
                            value?.principal?.atc_partner_manager_first_name,
                          managerLastName:
                            value?.principal?.atc_partner_manager_last_name,
                          managerContact:
                            value?.principal?.atc_partner_manager_contact,
                          partnerFirstName:
                            value?.principal?.atc_partner_first_name,
                          partnerLastName:
                            value?.principal?.atc_partner_last_name,
                          partnerContact: value?.principal?.atc_partner_contact,
                          CosFirstName:
                            value?.principal?.atc_partner_cos_first_name,
                          CosLastName:
                            value?.principal?.atc_partner_cos_last_name,
                          CosContact: value?.principal?.atc_partner_cos_contact,
                        },
                      };

                      setProjectRecipients(newProjectRecipients);

                      setSelectedAtcPartner(
                        `${value?.principal?.atc_partner_first_name || ""} ${
                          value?.principal?.atc_partner_last_name || ""
                        }`.trim()
                      );
                    }}
                  />
                </HouseMemberColRight>
              </Col>
            </HouseMemberSection>

            <HouseMemberSection>
              <Col
                xs="12"
                md="6"
              >
                <FormLabel>
                  {"ATC Partner"}
                  {RequiredIndicator}
                </FormLabel>
                <p>{"Displays the Partner's Name"}</p>
              </Col>
              <Col
                xs="12"
                md="6"
              >
                <HouseMemberColRight>
                  <FormGenerator
                    type="text"
                    name="atcPartner"
                    placeholder="ATC Partner"
                    value={selectedAtcPartner}
                    isDisabled
                  />
                </HouseMemberColRight>
              </Col>
            </HouseMemberSection>
          </>
        )}

        {agencyForm}

        {additionalFields}

        <HouseMemberSection>
          <Col
            xs="12"
            md="6"
          >
            <FormLabel>
              {"Justification"}
              {agency === AGENCY_DPWH_ID && RequiredIndicator}
            </FormLabel>
            <p>{"Provide the justification"}</p>
          </Col>
          <Col
            xs="12"
            md="6"
          >
            <HouseMemberColRight>
              <FormGenerator
                type="textarea"
                name="justification"
                placeholder="Enter Text Here"
                rows="5"
                value={recipient?.justification || ""}
                onChange={(event) => {
                  const {
                    target: { value },
                  } = event;

                  const newProjectRecipients = [...projectRecipients];

                  newProjectRecipients[index] = {
                    ...newProjectRecipients[index],
                    justification: value,
                  };

                  setProjectRecipients(newProjectRecipients);
                }}
              />
            </HouseMemberColRight>
          </Col>
        </HouseMemberSection>

        {isOtherFormShown(recipient) && agency !== AGENCY_DPWH_ID && (
          <Others
            allocationName={
              projectRecipients[index]?.allocationContactName || ""
            }
            contact={projectRecipients[index]?.allocationContactNumber || ""}
            onChange={(updates) => {
              const newProjectRecipients = [...projectRecipients];

              newProjectRecipients[index] = {
                ...newProjectRecipients[index],
                ...updates,
              };
              setProjectRecipients(newProjectRecipients);
            }}
          />
        )}

        {/* TVI Courses table starts here */}
        {agency === AGENCY_TESDA_ID ? (
          <CoursesTable
            data={TVICourses}
            selectedCourses={selectedCourses}
            setSelectedCourses={setSelectedCourses}
            form={projectRecipients}
            setForm={setProjectRecipients}
            idx={index}
          />
        ) : null}
        {/* TVI Courses table end here */}
      </>
    );
  };

  return generateForm();
};

AddProgramForm.prototype = {
  agency: PropTypes.number.isRequired,
  handleSearchInputOnChange: PropTypes.func,
  index: PropTypes.number.isRequired,
  handleCongressmanSearch: PropTypes.func,
  handleDeleteRow: PropTypes.func,
  setProjectRecipients: PropTypes.func,
  regionOptions: PropTypes.oneOfType([PropTypes.object]),
  recipient: PropTypes.oneOfType([PropTypes.object]),
  projectRecipients: PropTypes.oneOfType([PropTypes.object]),
  selectedProject: PropTypes.number,
};

export default AddProgramForm;
